<script setup>
import { ref, provide, onMounted, computed } from 'vue';
import { on as busOn } from '/app/plugins/bus';

const props = defineProps({
    immediate: {
        type: Boolean,
        default: false,
    },
    showGuide: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['next', 'exit', 'done', 'close']);

const context = ref({});
const container = ref(null);
const guides = ref([]);
const activeIndex = ref(-1);
const isLast = computed(() => activeIndex.value === guides.value.length - 1);

const setIndex = (target) => {
    if (typeof target === 'function') {
        activeIndex.value = target(activeIndex.value);
        return;
    }
    activeIndex.value = target;
};

const tourApi = {
    guides,
    isLast,
    context,
    activeIndex,
    setIndex,
    registerGuide: (id, dataRef) => {
        if (!guides.value.find(guide => guide.id === id)) {
            guides.value = [...guides.value, { id, dataRef }];
        }
    },
    unregisterGuide: (id) => {
        const guide = guides.value.find(guide => guide.id === id);
        const index = guides.value.indexOf(guide);
        if (index !== -1) {
            guides.value.splice(index, 1);
        }
    },
    goToGuide: (id) => {
        const guide = guides.value.find(guide => guide.id === id);
        const index = guides.value.indexOf(guide);
        if (index !== -1) {
            setIndex(index);
        }
    },
    nextGuide: () => {
        const nextIdx = activeIndex.value + 1;
        if (nextIdx < guides.value.length) {
            setIndex(nextIdx);
            emit('next', { id: guides.value[nextIdx].id });
        }
    },
    exit: () => {
        emit('exit', context.value);
        if (activeIndex.value === guides.value.length - 1) {
            emit('done', context.value);
        }
        setIndex(-1);
    },
    close: () => {
        emit('close', context.value);
        if (activeIndex.value === guides.value.length - 1) {
            emit('done', context.value);
        }
        setIndex(-1);
    }
};

provide('tour', tourApi);
provide('is-last', isLast);

defineExpose({
    goToGuide: tourApi.goToGuide,
    exit: tourApi.exit,
});

busOn('tour:next', ({ guide, payload, scrollToTop, delay = 0 } = {}) => {
    setTimeout(() => {
        if (payload) {
            context.value = payload;
        }
        tourApi.goToGuide(guide);
        if (scrollToTop && container.value) {
            container.value.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, delay);
});

onMounted(() => {
    if (props.immediate && props.showGuide) {
        tourApi.goToGuide(props.showGuide);
    }
});
</script>

<template>
    <div ref="container">
        <slot v-bind="{ context, goToGuide: tourApi.goToGuide, nextGuide: tourApi.nextGuide, exit: tourApi.exit }" />
    </div>
</template>
